import { ReadonlyVec3 } from 'gl-matrix';
import { ChangeTracker } from '../changes/ChangeTracker';
import { RoiSnapshot, TrackableObject } from '../changes/DomainChange';
import { newRoiId, RoiId } from '../changes/ids';
import { DicomColor } from '../dicom/dicomColor';
import { ContourEditBlocker } from '../dicom/contourEditBlocker';
import type { RecordedRoiReview } from '../provenance/RecordedRoiReview';
import {
    isSameContourStep,
    type ContourStepMove,
    type ContourStepRef,
} from '../undo/ContourEditStep';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomRoi } from '../dicom/dicomRoi';
import {
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import {
    ApprovedStructureError,
    DomainValidationError,
    LockedStructureError,
} from '../errors/DomainError';

export type RoiCategory =
    | 'Target'
    | 'OAR'
    | 'External'
    | 'Marker'
    | 'Support'
    | 'Other';

function deriveCategory(t: RoiInterpretedType | undefined): RoiCategory {
    switch (t) {
        case RoiInterpretedType.PTV:
        case RoiInterpretedType.CTV:
        case RoiInterpretedType.GTV:
            return 'Target';
        case RoiInterpretedType.ORGAN:
        case RoiInterpretedType.OAR:
        case RoiInterpretedType.AVOIDANCE:
            return 'OAR';
        case RoiInterpretedType.EXTERNAL:
            return 'External';
        case RoiInterpretedType.MARKER:
        case RoiInterpretedType.REGISTRATION:
        case RoiInterpretedType.ISOCENTER:
            return 'Marker';
        case RoiInterpretedType.BOLUS:
        case RoiInterpretedType.SUPPORT:
        case RoiInterpretedType.FIXATION:
            return 'Support';
        default:
            return 'Other';
    }
}

/**
 * Inclusive bounds for a relative-density override that may be assigned to an
 * ROI (dimensionless, relative to water). Applied to both the relative electron
 * and relative mass density fields.
 */
export type DensityOverrideRange = {
    /** Smallest accepted value (inclusive). */
    min: number;
    /** Largest accepted value (inclusive). */
    max: number;
};

/**
 * Accepted range for a relative-density override, enforced at the domain
 * boundary (see {@link assertValidDensityOverride}). Relative densities are
 * non-negative by physics, and an upper bound of 20 comfortably covers dense
 * implant/metal overrides while still rejecting obviously mistyped values.
 *
 * This is the clinical acceptance bound and it lives in the domain so it is the
 * single source of truth: the setter rejects values outside it regardless of
 * caller (UI, event handler, or service), and the UI parser reads it back for
 * immediate feedback rather than re-declaring it.
 *
 * Deliberately a constant rather than a deployment config value: no site has
 * needed a different ceiling, and a tunable clinical acceptance range would be
 * a safety-relevant setting to validate and version. Revisit only with a real
 * clinical need — and then also revisit the requirement wording.
 */
export const DENSITY_OVERRIDE_RANGE: DensityOverrideRange = {
    min: 0,
    max: 20,
};

/**
 * Guards the density-override invariant at the domain boundary, so no caller —
 * UI, event handler, or service — can persist an out-of-range value.
 *
 * A relative electron/mass density override is a dimensionless ratio against
 * water. `undefined` is valid and means "no override" (the physical property is
 * omitted from the output RTSTRUCT). Any set value must be finite and within the
 * clinical acceptance range ({@link DENSITY_OVERRIDE_RANGE}, `[0, 20]`). The UI
 * parser (`parseDensityInput`) reuses the same constant for immediate feedback,
 * but the rule is enforced here too because the event/service APIs call this
 * setter directly, bypassing the UI.
 *
 * This is a call-site guard, not a construction-time invariant — a new writer
 * of `dicomRoi.electronDensityOverride` must call it too.
 */
function assertValidDensityOverride(
    value: number | undefined,
    kind: 'electron' | 'mass'
): void {
    if (value === undefined) {
        return;
    }
    if (
        !Number.isFinite(value) ||
        value < DENSITY_OVERRIDE_RANGE.min ||
        value > DENSITY_OVERRIDE_RANGE.max
    ) {
        throw new DomainValidationError(
            `Invalid ${kind} density override: expected a finite relative ` +
                `density within [${DENSITY_OVERRIDE_RANGE.min}, ` +
                `${DENSITY_OVERRIDE_RANGE.max}], got ${value}`
        );
    }
}

export class Roi implements TrackableObject {
    readonly trackableKind = 'roi' as const;
    readonly id: RoiId;
    private tracker: ChangeTracker | null = null;
    private _contourRevision = 0;
    private _propertiesRevision = 0;
    private _modelGenerated = false;
    private _contourEditedSinceGeneration = false;
    private _recordedReview: RecordedRoiReview | null = null;
    /**
     * The structure's own rejection that a geometry change cleared, with the
     * history step whose geometry it judged. A restore of that step brings
     * the rejection back; any review of the structure, and a save, forgets it.
     */
    private _clearedRejection: {
        readonly judged: ContourStepRef;
        readonly review: RecordedRoiReview | null;
    } | null = null;

    constructor(
        public readonly dicomRoi: DicomRoi,
        /** Owning structure set: ROI numbers repeat across sets. */
        public readonly structureSetUid: string,
        id: RoiId = newRoiId()
    ) {
        this.id = id;
    }

    get trackableId(): RoiId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    get roiNumber(): number {
        return this.dicomRoi.roiNumber;
    }

    get name(): string {
        return this.dicomRoi.name;
    }

    get color(): DicomColor {
        return this.dicomRoi.color;
    }

    get approvalStatus(): DICOMApprovalStatus {
        return this.dicomRoi.approvalStatus;
    }

    get isApproved(): boolean {
        return this.dicomRoi.isApproved;
    }

    get locked(): boolean {
        return this.dicomRoi.locked;
    }

    /** True when neither approval nor a lock blocks a geometry change. */
    get canModifyContours(): boolean {
        return this.dicomRoi.canModifyContours;
    }

    /**
     * Which guard blocks a geometry change, or undefined when none does;
     * approval outranks the lock.
     */
    get contourEditBlocker(): ContourEditBlocker | undefined {
        return this.dicomRoi.contourEditBlocker;
    }

    get interpretedType(): RoiInterpretedType {
        return this.dicomRoi.interpretedType;
    }

    get category(): RoiCategory {
        return deriveCategory(this.interpretedType);
    }

    get contourCount(): number {
        return this.dicomRoi.contours.length;
    }

    get isEmpty(): boolean {
        return this.contourCount === 0;
    }

    get isPointRoi(): boolean {
        return this.dicomRoi.isPointRoi;
    }

    /**
     * Volume in cubic mm, or null when not yet computed.
     * TODO §2.2 — volume calculation lives in the engine and is out of scope
     * for this task. Wire in the engine-provided value when the volume
     * pipeline is connected.
     */
    get volumeMm3(): number | null {
        return null;
    }

    rename(newName: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot rename approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.name = newName;
        this.tracker?.markChanged(this);
    }

    setColor(color: DicomColor): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot recolor approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.color = color;
        this.tracker?.markChanged(this);
    }

    approve(_reviewer: string): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.APPROVED;
        this._recordedReview = null;
        this._clearedRejection = null;
        this.tracker?.markChanged(this);
    }

    reject(_reviewer: string): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.REJECTED;
        this._recordedReview = null;
        this._clearedRejection = null;
        this.tracker?.markChanged(this);
    }

    unapprove(): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        this._recordedReview = null;
        this._clearedRejection = null;
        this.tracker?.markChanged(this);
    }

    /**
     * The structure's own review as the connector recorded it: who made it and
     * when. Null when none is recorded, and from the moment the review changes
     * in the session, until a save records the new one.
     */
    get recordedReview(): RecordedRoiReview | null {
        return this._recordedReview;
    }

    /**
     * Adopts the review the connector recorded for this structure. Not an
     * edit: it is load-time or save-time truth, so no change is reported.
     */
    adoptRecordedReview(review: RecordedRoiReview | null): void {
        this._clearedRejection = null;
        this._recordedReview =
            review !== null && review.status === this.approvalStatus
                ? review
                : null;
    }

    setInterpretedType(type: RoiInterpretedType): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change interpreted type of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.interpretedType = type;
        this.tracker?.markChanged(this);
    }

    setIdentificationCode(code: RoiIdentificationCodeSequence): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change identification code of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.identificationCode = code;
        this.tracker?.markChanged(this);
    }

    /**
     * Visibility is pure rendering state — editable even when the ROI
     * is approved (you can still show/hide an approved structure).
     */
    setVisibility(visible: boolean): void {
        this.dicomRoi.visible = visible;
        this.tracker?.markChanged(this);
    }

    /**
     * Deliberately togglable on an approved ROI: unlocking never grants edit
     * rights (`canModifyContours` still requires approval to be clear), and
     * a disabled lock control would be impossible to explain.
     *
     * Tracked as a change, unlike visibility and line thickness: the lock is
     * stored with the structure set, so a toggle is unsaved work and has to be
     * reported as such — a lock that only reached the database when some other
     * edit happened to be saved would be lost without a word. The snapshot
     * carries the value, so no revision counter is needed.
     */
    setLocked(locked: boolean): void {
        this.dicomRoi.locked = locked;
        this.tracker?.markChanged(this);
    }

    /**
     * Contour line thickness is pure rendering state — editable even
     * when the ROI is approved.
     */
    setContourThickness(thickness: number): void {
        this.dicomRoi.contourThickness = thickness;
        this.tracker?.markChanged(this);
    }

    setDescription(description: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change description of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.roiDescription = description;
        this._propertiesRevision++;
        this.tracker?.markChanged(this);
    }

    setGenerationAlgorithm(algorithm: RoiGenerationAlgorithmType): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change generation algorithm of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.roiGenerationAlgorithm = algorithm;
        this._propertiesRevision++;
        this.tracker?.markChanged(this);
    }

    setElectronDensityOverride(value: number | undefined): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change electron density override of approved ROI ${this.dicomRoi.name}`
            );
        }
        assertValidDensityOverride(value, 'electron');
        this.dicomRoi.electronDensityOverride = value;
        this._propertiesRevision++;
        this.tracker?.markChanged(this);
    }

    setMassDensityOverride(value: number | undefined): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change mass density override of approved ROI ${this.dicomRoi.name}`
            );
        }
        assertValidDensityOverride(value, 'mass');
        this.dicomRoi.massDensityOverride = value;
        this._propertiesRevision++;
        this.tracker?.markChanged(this);
    }

    setPointCoordinates(coordinatesMm: ReadonlyVec3): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change point coordinates of approved ROI ${this.dicomRoi.name}`
            );
        }
        // A point's coordinates ARE its geometry, so unlike the metadata
        // setters above this honours the full geometry guard: parent-set
        // approval (via canModify), then the edit lock — a locked isocenter
        // must not be movable, and approval outranks the lock.
        if (!this.dicomRoi.canModify) {
            throw new ApprovedStructureError(
                `Cannot change point coordinates of ROI ` +
                    `${this.dicomRoi.name} in an approved structure set`
            );
        }
        if (this.locked) {
            throw new LockedStructureError(
                `Cannot change point coordinates of locked ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.setPointCoordinates(coordinatesMm);
        // A point move is a geometry change — same dirty class as a
        // contour edit.
        this.advanceContourRevision(null, null);
        this.tracker?.markChanged(this);
    }

    /**
     * The AI model whose result the owning set is produced this structure.
     * Like the edit lock, not a DICOM attribute: the connector records it, and
     * it is never inferred from the ROI Generation Algorithm tag.
     */
    get modelGenerated(): boolean {
        return this._modelGenerated;
    }

    /**
     * The contour of a model-generated structure has been changed since the
     * model produced it. Only ever turns on: an edit later undone is still an
     * edit someone made. Always false for a structure the model did not produce.
     */
    get contourEditedSinceGeneration(): boolean {
        return this._contourEditedSinceGeneration;
    }

    /** Marks this structure as produced by the model, unedited, when its result is loaded. */
    markModelGenerated(): void {
        this._modelGenerated = true;
        this._contourEditedSinceGeneration = false;
    }

    /**
     * Adopts what the connector recorded about this structure's generation. Not
     * an edit: it is load-time truth, so no change is reported to the tracker.
     */
    adoptModelGeneration(
        modelGenerated: boolean,
        contourEdited: boolean
    ): void {
        this._modelGenerated = modelGenerated;
        this._contourEditedSinceGeneration = modelGenerated && contourEdited;
    }

    /**
     * Engine signals contour pixels were edited; domain records the change.
     * Every contour change funnels here (whatever the tool), which is what
     * makes it the place a model-generated structure becomes edited.
     */
    markContourEdited(previousStep: ContourStepRef | null): void {
        this.advanceContourRevision(previousStep, null);
        if (this._modelGenerated) {
            this._contourEditedSinceGeneration = true;
        }
        this.tracker?.markChanged(this);
    }

    /**
     * Undo restored prior pixel state; emit a changeset entry so adapters
     * resync. Deliberately also counts as a contour change: an undo back to the
     * loaded baseline still leaves the revision advanced, so the worst case
     * here is saving byte-identical content.
     *
     * This marks the DOMAIN ROI only, which is what decides whether the
     * structure set is offered to the save at all. Whether the restored pixels
     * actually reach DICOM is decided separately, by the engine ROI's own
     * modification flag — see RegionOfInterest.restorePixelsFromCompressed.
     * Both have to be set; a revision bump on its own buys a save that skips
     * the contour and still reports success.
     */
    markContourReverted(steps: ContourStepMove | null): void {
        this.advanceContourRevision(steps?.from ?? null, steps?.to ?? null);
        this.tracker?.markChanged(this);
    }

    /**
     * Records a change to the structure's geometry: a contour edit, an undo or
     * redo, or a point move. The structure's own rejection judged the geometry
     * as it was, so the structure returns to unapproved and awaits a new
     * review. A restore of the step a cleared rejection judged brings that
     * rejection back, reviewer included, when the structure's contours can
     * change. A rejection of the whole set stays: it is the set's decision,
     * which only the set's own review changes.
     *
     * @param left The history step the geometry leaves, or null when the
     *   change has no place in the contour history.
     * @param restored The history step a restore returns to; null for a new
     *   edit.
     */
    private advanceContourRevision(
        left: ContourStepRef | null,
        restored: ContourStepRef | null
    ): void {
        this._contourRevision++;
        if (
            this.dicomRoi.structureSetApprovalStatus !==
            DICOMApprovalStatus.UNAPPROVED
        ) {
            return;
        }
        if (this.dicomRoi.approvalStatus === DICOMApprovalStatus.REJECTED) {
            this._clearedRejection =
                left === null
                    ? null
                    : { judged: left, review: this._recordedReview };
            this.dicomRoi.approvalStatus = DICOMApprovalStatus.UNAPPROVED;
            this._recordedReview = null;
        } else if (
            this.dicomRoi.approvalStatus === DICOMApprovalStatus.UNAPPROVED &&
            this.dicomRoi.canModifyContours &&
            restored !== null &&
            this._clearedRejection !== null &&
            isSameContourStep(this._clearedRejection.judged, restored)
        ) {
            this.dicomRoi.approvalStatus = DICOMApprovalStatus.REJECTED;
            this._recordedReview = this._clearedRejection.review;
            this._clearedRejection = null;
        }
    }

    captureSnapshot(): RoiSnapshot {
        return {
            id: this.id,
            roiNumber: this.roiNumber,
            structureSetUid: this.structureSetUid,
            name: this.name,
            color: { ...this.color },
            approvalStatus: this.approvalStatus,
            interpretedType: this.interpretedType,
            contourCount: this.contourCount,
            volumeMm3: this.volumeMm3,
            isEmpty: this.isEmpty,
            isPointRoi: this.isPointRoi,
            locked: this.locked,
            contourRevision: this._contourRevision,
            propertiesRevision: this._propertiesRevision,
        };
    }
}
